import type { ReactNode } from "react";
import { useContactInfo } from "./ContactInfoProvider";
import { useMarketingConfig } from "./MarketingConfigProvider";
import { whatsappHrefFor } from "@/lib/site-contact";
import {
  useMessageVariant,
  genericMessage,
  buildTopicMessage,
  withBusinessHoursNote,
  type MessageIntent,
} from "@/lib/whatsapp-message";

/**
 * Enlace a WhatsApp compartido por todo el sitio público. Centraliza tanto
 * el mensaje prellenado (genérico, o el `topic` de la página actual —
 * servicio, servicio+distrito, post de blog — con tono A/B y nota de fuera
 * de horario aplicados aquí una sola vez) como el tracking de clic: siempre
 * manda el evento GA4 `whatsapp_click` (con el tema y la variante usados, y
 * cualquier utm_source/utm_medium/utm_campaign de la URL de aterrizaje), y
 * si hay conversion_id + whatsapp label configurados en Preferencias →
 * Config CRM, dispara también la conversión de Google Ads correspondiente.
 * Como el link abre en pestaña nueva (`target="_blank"`), el evento nunca
 * se corta por la navegación.
 */
export function WhatsAppLink({
  className,
  children,
  ariaLabel,
  onClick,
  topic,
  intent = "cotizar",
  topicId = "generico",
}: {
  className?: string;
  children: ReactNode;
  ariaLabel?: string;
  /** Se llama además del tracking de clic (p. ej. cerrar el menú móvil). */
  onClick?: () => void;
  /** Tema puntual de esta página (p. ej. "Activaciones BTL", "Activaciones BTL en Miraflores"). Sin esto, usa el mensaje genérico de marca. */
  topic?: string;
  /** Tono del mensaje cuando hay `topic`: "cotizar" (default) o "consultar" (blog). */
  intent?: MessageIntent;
  /** Id corto para segmentar el evento GA4 whatsapp_click por origen — no afecta el texto del mensaje. */
  topicId?: string;
}) {
  const contact = useContactInfo();
  const { googleAdsConversionId, googleAdsWhatsappConversionLabel } = useMarketingConfig();
  const variant = useMessageVariant();
  const baseMessage = topic ? buildTopicMessage(topic, intent, variant) : genericMessage(variant);
  const finalMessage = withBusinessHoursNote(baseMessage);

  function handleClick() {
    if (typeof gtag === "function") {
      const search = new URLSearchParams(window.location.search);
      const utmParams: Record<string, string> = {};
      for (const key of ["utm_source", "utm_medium", "utm_campaign"]) {
        const value = search.get(key);
        if (value) utmParams[key] = value;
      }
      gtag("event", "whatsapp_click", {
        event_category: "engagement",
        topic_id: topicId,
        message_variant: variant,
        ...utmParams,
      });
      if (googleAdsConversionId && googleAdsWhatsappConversionLabel) {
        gtag("event", "conversion", {
          send_to: `${googleAdsConversionId}/${googleAdsWhatsappConversionLabel}`,
          value: 1.0,
          currency: "PEN",
        });
      }
    }
    onClick?.();
  }

  return (
    <a
      href={whatsappHrefFor(contact.whatsapp, finalMessage)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      onClick={handleClick}
      className={className}
    >
      {children}
    </a>
  );
}
